import React, { useMemo } from 'react';
import { FormikProps } from 'formik';
import * as Yup from 'yup';

import { FormikTextInput } from 'src/common/form/formik/text-input';
import { JourneyFormik, JourneyForm } from 'src/common/JourneyFormik';
import { apiCreateUserApiKey } from 'src/utils/journeyApi';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { ErrorInterface } from 'src/common/interfaces/error.interface';
import { IntegrationApiKey } from 'src/common/interfaces/integration-api-key.interface';
import SlideOver from 'src/common/components/slide-over';
import { Integration } from 'src/common/interfaces/integration.interface';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  provider: Integration;
  onApiKeyCreate: (response: IntegrationApiKey) => void;
}

const validationSchema = Yup.object({ api_key: Yup.string().required('Required') });

const SlideOverPropertyMapper = (code: string, name: string) =>
  code === 'hubspot-tracker'
    ? { slideOverTitle: 'Hubspot tracking code', inputType: 'tel', inputLabel: `${name} tracking code` }
    : code === 'segment'
    ? { slideOverTitle: 'New Segment integration', inputType: 'text', inputLabel: `${name} write key` }
    : { slideOverTitle: 'New API key', inputType: 'text', inputLabel: `${name} write key` };

export const DashboardIntegrationsApiKeyPanel = ({ isOpen, onClose, provider, onApiKeyCreate }: Props) => {
  const { code, parentCode, name, sidebar } = provider;
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();

  const integrationCode = parentCode || code;

  const { slideOverTitle, inputType, inputLabel } = SlideOverPropertyMapper(code, name);

  const formikParams = useMemo(
    () => ({
      initialValues: { api_key: '' },
      validationSchema,
      onSubmit: (values: Record<string, string>) => {
        apiCreateUserApiKey(integrationCode, values.api_key)
          .then((response) => {
            onApiKeyCreate(response);
            onClose();
            setSuccessNotification(`${name} successfully connected`);
          })
          .catch((error) => {
            setErrorNotification((error as ErrorInterface).message);
          });
      },
    }),
    [name, onApiKeyCreate, onClose, setSuccessNotification, setErrorNotification]
  );

  return (
    <SlideOver
      type='integration-api-key'
      title={slideOverTitle}
      open={isOpen}
      maxWidth='auto'
      panelClassName='w-[368px]'
      titleClassName='text-bedrock-serif-h2'
      onClose={onClose}
    >
      <JourneyFormik {...formikParams}>
        {({ values }: FormikProps<Record<string, string>>) => {
          const isFormValid = validationSchema.isValidSync(values);

          return (
            <JourneyForm className='flex flex-col space-y-4'>
              {sidebar?.description}
              <FormikTextInput
                id='api-key-input'
                placeholder={sidebar?.placeholder || 'Paste write key here'}
                required
                name='api_key'
                type={inputType}
                label={inputLabel}
              />
              <button type='submit' className='flex self-start btn-bedrock-primary' disabled={!isFormValid}>
                Connect
              </button>
            </JourneyForm>
          );
        }}
      </JourneyFormik>
    </SlideOver>
  );
};
